import React, { useState, useEffect, useRef } from 'react';
import type { CashlessWalletProduct, CashlessWalletOption } from '@/lib/TicketApi';
import { shopVar } from '@/lib/shopStyles';
import { Money } from '@/lib/money';
import { useLanguage } from '@/providers/LanguageProvider';
import { useCart } from '@/providers/CartProvider';
import { ulid } from 'ulidx';
import ProductAvailability from './ProductAvailability';
import { isFuture, parseISO } from "date-fns";
import * as m from "@/paraglide/messages.js"

interface CashlessWalletProductProps {
    product: CashlessWalletProduct;
}

const CashlessWalletProductComponent: React.FC<CashlessWalletProductProps> = ({ product }) => {
    const { currentLanguage: lang } = useLanguage();
    const { setProductQuantity, cart, lineQuantity } = useCart();
    const [selected, setSelected] = useState<number | 'custom' | null>(null);
    // FAS-1053 — the cart line this picker is editing. A line that already holds a top-up is left
    // alone when another amount is chosen: that starts a SECOND line, so one order can carry two
    // top-ups of different amounts.
    const activeLineIdRef = useRef<string | null>(null);
    const [rawCustomAmount, setRawCustomAmount] = useState<string>('');
    const [validationError, setValidationError] = useState<string | null>(null);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const isEditingCustom = useRef(false);
    const lastInvalidInput = useRef(false);

    const options: CashlessWalletOption[] = product.cashlessWallet.options || [];
    const custom = product.cashlessWallet.customTopup != null;
    const minAmount = product.cashlessWallet.customTopup?.lowerLimit.amount ?? 0;
    const maxAmount = product.cashlessWallet.customTopup?.upperLimit.amount ?? Infinity;
    const override = product.cashlessWallet.priceOverridePercentage;

    function isProductAvailable(): boolean {
        switch (product.availability.type) {
            case "available": return true;
            case "available_between": {
                const { availableFrom, availableUntil } = product.availability;
                const startDate = availableFrom ? parseISO(availableFrom) : null;
                const endDate = availableUntil ? parseISO(availableUntil) : null;
                if (startDate !== null && isFuture(startDate)) return false;
                return !(endDate !== null && !isFuture(endDate));
            }
            case "notAvailable": return false;
            case "notListed": return false;
            default: return false;
        }
    }

    const productIsAvailable = isProductAvailable();

    function parseEuroInputToCents(input: string): number {
        if (!input) return 0;
        const safe = input.replace(/\D/g, '');
        const intVal = parseInt(safe, 10);
        if (isNaN(intVal)) return 0;
        return intVal * 100;
    }

    function formatCentsToEuroString(amount: number): string {
        if (isNaN(amount)) return '';
        return String(Math.floor(amount / 100));
    }

    function displayPrice(amountCents: number, currency: string): string {
        if (override != null) {
            const discounted = Math.round(amountCents * override / 100);
            return Money.fromInteger(discounted, currency).display(lang);
        }
        return Money.fromInteger(amountCents, currency).display(lang);
    }

    const beginLine = (): string => {
        const active = activeLineIdRef.current;
        activeLineIdRef.current = active !== null && lineQuantity(active) === 0 ? active : ulid();
        return activeLineIdRef.current;
    };

    // Sync with cart on mount or cart change
    useEffect(() => {
        if (!cart) {
            activeLineIdRef.current = null;
            return;
        }
        if (isEditingCustom.current) return;
        if (lastInvalidInput.current) return;
        const cartProduct = activeLineIdRef.current !== null
            ? cart.products.find(p => p.lineId === activeLineIdRef.current)
            : cart.products.filter(p => p.productId === product.productId).at(-1);
        if (cartProduct) {
            activeLineIdRef.current = cartProduct.lineId;
        }
        if (!cartProduct || !cartProduct.settings) {
            setSelected(null);
            setRawCustomAmount('');
            return;
        }
        const { amount, currency } = cartProduct.settings;
        if (typeof amount !== 'number' || !currency) {
            setSelected(null);
            setRawCustomAmount('');
            return;
        }
        const idx = options.findIndex(opt => opt.amount === amount && opt.currency === currency);
        if (idx !== -1) {
            setSelected(idx);
            setRawCustomAmount('');
            setValidationError(null);
        } else {
            setSelected('custom');
            setRawCustomAmount(formatCentsToEuroString(amount));
            setValidationError(null);
        }
    }, [cart, product.productId, options, lang]);

    const handleSelect = (idx: number | 'custom') => {
        if (selected === idx) {
            setProductQuantity(product.productId, 0, undefined, activeLineIdRef.current ?? undefined);
            activeLineIdRef.current = null;
            setSelected(null);
            setRawCustomAmount('');
            setValidationError(null);
            lastInvalidInput.current = false;
            return;
        }
        setSelected(idx);
        setValidationError(null);
        lastInvalidInput.current = false;
        if (idx === 'custom') {
            beginLine();
            setTimeout(() => inputRef.current?.focus(), 0);
            return;
        }
        const option = options[idx as number];
        setProductQuantity(product.productId, 1, {
            amount: option.amount,
            currency: option.currency
        }, beginLine());
        setRawCustomAmount('');
    };

    const validateAndUpdateCart = (value: string) => {
        const cents = parseEuroInputToCents(value);
        const lineId = activeLineIdRef.current ?? beginLine();
        if (!value || cents <= 0) {
            setValidationError(null);
            setProductQuantity(product.productId, 0, undefined, lineId);
            lastInvalidInput.current = false;
            return;
        }
        if (cents < minAmount) {
            setValidationError(m.wallet_minimum_amount({ amount: Money.fromInteger(minAmount, product.price.currency).display(lang) }));
            setProductQuantity(product.productId, 0, undefined, lineId);
            lastInvalidInput.current = true;
            return;
        }
        if (cents > maxAmount) {
            setValidationError(m.wallet_maximum_amount({ amount: Money.fromInteger(maxAmount, product.price.currency).display(lang) }));
            setProductQuantity(product.productId, 0, undefined, lineId);
            lastInvalidInput.current = true;
            return;
        }
        setValidationError(null);
        setProductQuantity(product.productId, 1, {
            amount: cents,
            currency: product.price.currency
        }, lineId);
        setSelected('custom');
        lastInvalidInput.current = false;
    };

    const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        isEditingCustom.current = true;
        const value = e.target.value.replace(/\D/g, '');
        setRawCustomAmount(value);
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            validateAndUpdateCart(value);
            isEditingCustom.current = false;
        }, 500);
    };

    const inputValue = selected === 'custom' ? rawCustomAmount : '';
    const hasDescription = product.description && product.description.trim() !== '';

    return (
        <div
            className="border rounded-md px-4 pt-4 pb-2 mb-4 shadow-md last:mb-0 overflow-hidden"
            style={{ borderColor: shopVar('--shop-borderColor') }}
        >
            <div className="flex items-start border-b border-dashed w-full pb-4" style={{ borderColor: shopVar('--shop-borderColor') }}>
                <div className="flex-grow pr-2">
                    <div className="flex items-center">
                        <h2 className="text-base font-bold mr-2" style={{ color: shopVar('--shop-textColor') }}>{product.name}</h2>
                    </div>
                    {hasDescription && (
                        <p className="font-normal text-xs tracking-tight mt-1" style={{ color: shopVar('--shop-textColor') }}>
                            {product.description}
                        </p>
                    )}
                </div>
            </div>
            {productIsAvailable && (
                <div className="flex flex-wrap gap-2 mt-4">
                    {options.map((opt: CashlessWalletOption, idx: number) => (
                        <button
                            key={idx}
                            className={`flex-1 min-w-[80px] max-w-[160px] px-4 py-2 rounded border text-center font-semibold transition-colors ${selected === idx ? 'ring-2 ring-black' : ''}`}
                            style={{
                                background: selected === idx ? shopVar('--shop-addToCartButtonColor') : shopVar('--shop-panelBackgroundColor'),
                                color: selected === idx ? shopVar('--shop-addToCartButtonTextColor') : shopVar('--shop-textColor'),
                                borderColor: shopVar('--shop-borderColor'),
                            }}
                            onClick={() => handleSelect(idx)}
                            type="button"
                        >
                            <span>{Money.fromInteger(opt.amount, opt.currency).display(lang)}</span>
                            {override != null && (
                                <span className="block text-xs opacity-75 mt-0.5">
                                    {displayPrice(opt.amount, opt.currency)}
                                </span>
                            )}
                        </button>
                    ))}
                    {custom && (
                        <button
                            className={`flex-1 min-w-[80px] max-w-[160px] px-4 py-2 rounded border text-center font-semibold transition-colors ${selected === 'custom' ? 'ring-2 ring-black' : ''}`}
                            style={{
                                background: selected === 'custom' ? shopVar('--shop-addToCartButtonColor') : shopVar('--shop-panelBackgroundColor'),
                                color: selected === 'custom' ? shopVar('--shop-addToCartButtonTextColor') : shopVar('--shop-textColor'),
                                borderColor: shopVar('--shop-borderColor'),
                            }}
                            onClick={() => handleSelect('custom')}
                            type="button"
                        >
                            {m.wallet_choose_custom()}
                        </button>
                    )}
                </div>
            )}
            {productIsAvailable && selected === 'custom' && (
                <div className="mt-4">
                    <label htmlFor="custom-wallet-amount-input" className="block text-xs font-medium mb-1" style={{ color: shopVar('--shop-textColor') }}>
                        {m.wallet_custom_amount()}
                    </label>
                    <div className="flex gap-2 items-center">
                        <span className="text-base font-semibold" style={{ color: shopVar('--shop-textColor') }}>{Money.getCurrencySymbol(product.price.currency)}</span>
                        <input
                            id="custom-wallet-amount-input"
                            ref={inputRef}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={inputValue}
                            onChange={handleCustomAmountChange}
                            placeholder="0"
                            className="flex-1 px-4 py-2 border rounded focus:ring-2 focus:ring-black text-base"
                            style={{ borderColor: shopVar('--shop-borderColor'), color: shopVar('--shop-textColor'), background: shopVar('--shop-panelBackgroundColor') }}
                            aria-invalid={!!validationError}
                            aria-describedby={validationError ? 'custom-wallet-amount-error' : undefined}
                            autoComplete="off"
                        />
                    </div>
                    {rawCustomAmount && !validationError && override != null && (
                        <div className="text-xs mt-1 opacity-75" style={{ color: shopVar('--shop-textColor') }}>
                            {m.wallet_price({ price: displayPrice(parseEuroInputToCents(rawCustomAmount), product.price.currency) })}
                        </div>
                    )}
                    {validationError && (
                        <div id="custom-wallet-amount-error" className="text-red-600 text-xs mt-1" role="alert">
                            {validationError}
                        </div>
                    )}
                </div>
            )}
            <div className="pt-2">
                <ProductAvailability product={product} requiresAmountSelection/>
            </div>
        </div>
    );
};

export default CashlessWalletProductComponent;
